import { useQuery, useMutation } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';
import type {
  AnnotatedJsonSchema,
  ConfigEntity,
  ConfigEntityGetEndpointResponse,
  EnvironmentKey,
  DeepCloneEndpointResponse,
  ConfigEntityDetail,
} from '@web-config-app/core';
import {
  pipeFunctions,
  getConfigEntitySchemaWithDomainEntityAttributes,
  ConfigEntityAuthAction,
  hasEntityOperation,
} from '@web-config-app/core';
import { buildConfigEntityAuthHeaders } from '@web-config-app/api';
import { getEntityEndpoint } from '../../utilities/get-entity-endpoint/get-entity-endpoint.util';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';
import { getEndpointParamsWithValues } from '../../utilities/get-endpoint-params-with-values/get-endpoint-params-with-values';
import { getIdParam } from '../../utilities/get-id-param/get-id-param';
import { getIncludeParam } from '../../utilities/get-include-param/get-include-param';
import { injectOperationEntityMetadataRequiredFields } from '../../utilities/inject-operation-entity-metadata-required-fields/inject-operation-entity-metadata-required-fields';
import {
  OnMutateWithOperation,
  OnSuccessWithEnvironment,
} from '../../types/operation.types';
import { injectDomainEntityId } from '../../utilities/inject-domain-entity-id/inject-domain-entity-id';
import { useProcessIncludedEntity } from './use-process-included-entity';
import { removeDomainEntityId } from '../../utilities/remove-domain-entity-id/remove-domain-entity-id';
import { transposeIdToSourceId } from '../../utilities/transpose-id-to-source-id/transpose-id-to-source-id';
import { regenerateGeneratedIds } from '../../utilities/regenerate-generated-ids/regenerate-generated-ids';
import { useLoadEntityRelationshipsSchemas } from '../use-load-entity-relationships-schemas/use-load-entity-relationships-schemas';
import { getIncludeEntityTypes } from '../../utilities/get-included-entity-types/get-included-entity-types';
import { addMetaFieldToBodyData } from '../../utilities/add-meta-field-to-body-data/add-meta-field-to-body-data';
import { useUserInfo } from '../use-user-info/use-user-info';
import { batchAsyncOperations } from '../../utilities/batch-async-operations/batch-async-operations';
import { useConfigEntityGetByIdQuery } from '../use-config-entity-get-by-id-query/use-config-entity-get-by-id-query';
import { isConfigEntityDetail } from '../../utilities/is-config-entity-detail/is-config-entity-detail';
import { syncRelationshipsFromSchema } from '../../utilities/sync-relationships-from-schema/sync-relationships-from-schema';
import type { LoadEntityRelationshipsSchema } from '../../types/operation.types';

/**
 * Syncs relationship data for included entities by loading the appropriate
 * schemas and running `syncRelationshipsFromSchema` on each entity.
 *
 * Included entities that share the root entity's type reuse the root schema,
 * while entities of a different type require loading their own schema.
 */
async function syncIncludedEntityRelationships({
  includedEntities,
  entity,
  loadEntityRelationshipsSchemas,
}: {
  includedEntities: ConfigEntityDetail[];
  entity: ConfigEntity;
  loadEntityRelationshipsSchemas: LoadEntityRelationshipsSchema;
}): Promise<ConfigEntityDetail[]> {
  const domainRelationshipsSchema =
    entity.domainEntity.schema.properties?.relationships;
  const rootDomainType = entity.domainEntity.type;

  // Load schemas for all included entity types that differ from the root
  // type — entities sharing the root type reuse entity.schema directly.
  // We load ALL non-root types (not just direct relationships of the root)
  // because include=allReferences can return transitive types (e.g. activities
  // that belong to child campaigns, not the root campaign).
  const typesToLoad = [
    ...new Set(
      includedEntities
        .map((e) => e.attributes.entityMetadata.domainEntityType)
        .filter((t): t is string => Boolean(t) && t !== rootDomainType),
    ),
  ];

  const relationshipSchemas =
    typesToLoad.length > 0
      ? await loadEntityRelationshipsSchemas(typesToLoad)
      : [];

  return includedEntities.map((incEntity) => {
    const incDomainType = incEntity.attributes.entityMetadata.domainEntityType;

    // For included entities that share the root entity's type (e.g. child
    // campaigns under a root campaign), use entity.schema — it has the
    // correct domainEntityAttributes with x-entity-reference annotations.
    // The relationships schema is passed separately since entity.schema
    // (form schema) lacks a relationships property.
    let schemaForSync: AnnotatedJsonSchema = entity.schema;
    let relSchemaForSync: AnnotatedJsonSchema | undefined =
      domainRelationshipsSchema as AnnotatedJsonSchema | undefined;

    if (incDomainType !== rootDomainType) {
      const matchingSchema = relationshipSchemas.find(
        (rSchema) => rSchema.type === incDomainType,
      );
      if (!matchingSchema?.schema || matchingSchema.status === 'error') {
        // Schema unavailable — skip relationship sync for this entity.
        // The entity is still usable, just with potentially stale relationships.
        return incEntity;
      }
      schemaForSync = getConfigEntitySchemaWithDomainEntityAttributes(
        entity.schema,
        matchingSchema.schema,
      );
      relSchemaForSync = matchingSchema.schema.properties?.relationships as
        | AnnotatedJsonSchema
        | undefined;
    }

    return syncRelationshipsFromSchema(
      incEntity,
      schemaForSync,
      relSchemaForSync,
    );
  });
}

export type DeepCloneRequestOptions = Pick<
  UseMutationOptions<DeepCloneEndpointResponse, Error, DeepCloneRequestBody>,
  'onError' | 'onSettled'
> & {
  onMutate?: OnMutateWithOperation;
  fetchFn?: (path: string, options: any) => Promise<Response>;
  onSuccess?: OnSuccessWithEnvironment;
};

export type UseEntityDeepCloneOptions = {
  fetchFn?: (path: string, options: any) => Promise<Response>;
  prefetch?: UseQueryOptions<ConfigEntityGetEndpointResponse>;
  request?: DeepCloneRequestOptions;
};

export interface UseEntityDeepCloneProps {
  entity: ConfigEntity;
  instanceId: string | undefined;
  environment: EnvironmentKey;
  options?: UseEntityDeepCloneOptions;
}

export type UseEntityDeepCloneResult = ReturnType<typeof useEntityDeepClone>;

export interface DeepCloneRequestBody extends ConfigEntityDetail {
  data: ConfigEntityDetail;
  included: ConfigEntityDetail[];
}

export const useEntityDeepClone = ({
  entity,
  instanceId,
  environment,
  options,
}: UseEntityDeepCloneProps) => {
  // `entity.endpoints.deepClone` is optional at the type level (configEntity endpoints are
  // filtered to the operations the entity opts into). When it's missing we leave queryFn /
  // mutationFn undefined and surface that via `enableAction: false`, mirroring the convention
  // in useEntityCreate / useEntityValidate. Callers gate their UI on `enableAction`.
  const deepCloneEndpoint = entity.endpoints.deepClone;
  const deepClonePrefetchOperation = deepCloneEndpoint?.prefetch;
  const { apiUrl, generateDomainEntityId } = entity.domainEntity;

  const currentEnvironmentFetch = useEnvironmentFetch(environment, apiUrl);
  const { userInfo } = useUserInfo();
  const loadEntityRelationshipsSchemas = useLoadEntityRelationshipsSchemas();

  const getSyncedIncludedEntityById = useConfigEntityGetByIdQuery({
    sourceEnvironment: environment,
    includeReferences: false,
    domainEntityType: entity.domainEntity.type,
  });

  const processIncludedEntity = useProcessIncludedEntity();

  let prefetchQueryFn;
  let mutationFn;

  if (deepCloneEndpoint && deepClonePrefetchOperation) {
    const { endpointFetch: deepCloneGetFetch } =
      getEntityEndpoint<ConfigEntityGetEndpointResponse>(
        deepClonePrefetchOperation,
        options?.fetchFn ?? currentEnvironmentFetch,
      );

    // `deepCloneEndpoint` itself is the request endpoint — its `prefetch` sub-field is
    // unused by `getEntityEndpoint` / `injectOperationEntityMetadataRequiredFields` (both
    // only read `path`, `method`, `requiredEntityMetadataFieldValues`, etc.).
    const { endpointFetch: deepCloneRequestFetch } =
      getEntityEndpoint<DeepCloneEndpointResponse>(
        deepCloneEndpoint,
        options?.fetchFn ?? currentEnvironmentFetch,
      );

    const prefetchParams = getEndpointParamsWithValues([
      getIdParam(
        deepClonePrefetchOperation.pathParameters ?? [],
        deepClonePrefetchOperation.path,
        instanceId,
      ),
      getIncludeParam(
        deepClonePrefetchOperation.queryParameters ?? [],
        undefined,
        true,
        // I wish this could be schema-driven
        'allReferences',
      ),
    ]);

    prefetchQueryFn = async () => {
      const response = await deepCloneGetFetch({
        params: prefetchParams,
        headers: buildConfigEntityAuthHeaders(
          entity.domainEntity.type,
          ConfigEntityAuthAction.Get,
        ),
      });

      // Sync the root entity's relationships to remove stale entries. Injected relationships (from x-entity-inject-reference)
      // are automatically preserved since their data property never exists
      // in API responses.
      const domainRelationships = entity.domainEntity.schema.properties
        ?.relationships as AnnotatedJsonSchema | undefined;
      const syncedRootData = syncRelationshipsFromSchema(
        response?.data,
        entity.schema,
        domainRelationships,
      );

      const includedEntities = response?.included ?? [];
      if (includedEntities.length === 0) {
        return { ...response, data: syncedRootData };
      }
      const { results: syncedIncludedEntities } = await batchAsyncOperations({
        items: includedEntities,
        operation: async (includedEntity) => {
          const syncedIncludedEntity = await getSyncedIncludedEntityById(
            includedEntity.id,
            includedEntity.attributes.entityMetadata.domainEntityType,
          );
          return syncedIncludedEntity.data;
        },
      });

      const syncedIncluded = await syncIncludedEntityRelationships({
        includedEntities: syncedIncludedEntities,
        entity,
        loadEntityRelationshipsSchemas,
      });

      return {
        ...response,
        data: syncedRootData,
        included: syncedIncluded.length > 0 ? syncedIncluded : undefined,
      };
    };

    mutationFn = async (deepCloneRequestData: DeepCloneRequestBody) => {
      const dataWithRequiredMetadataFields =
        injectOperationEntityMetadataRequiredFields(
          deepCloneRequestData?.data,
          deepCloneEndpoint,
        );

      const requestData = isConfigEntityDetail(dataWithRequiredMetadataFields)
        ? pipeFunctions(
            transposeIdToSourceId,
            removeDomainEntityId,
          )(dataWithRequiredMetadataFields)
        : undefined;

      // Add author meta field for config entities (if userInfo available)
      const requestDataWithMeta =
        userInfo && requestData
          ? addMetaFieldToBodyData(requestData, userInfo)
          : requestData;

      const requestDataWithGeneratedIds = regenerateGeneratedIds(
        generateDomainEntityId?.enabled
          ? injectDomainEntityId(generateDomainEntityId, requestDataWithMeta)
          : requestDataWithMeta,
        entity.schema,
      );

      const relationshipsSchema =
        entity.domainEntity.schema.properties?.relationships;
      const relationshipsSchemasTypes = relationshipsSchema
        ? getIncludeEntityTypes(
            Object.keys(relationshipsSchema.properties ?? {}),
            entity.domainEntity.schema,
          )
        : [];

      const relationshipSchemas = await loadEntityRelationshipsSchemas(
        relationshipsSchemasTypes,
      );

      const finalIncludedEntities =
        deepCloneRequestData?.included?.map((incEntity) => {
          const includedEntity = relationshipSchemas.find(
            (rSchema) =>
              rSchema.type ===
              incEntity.attributes.entityMetadata.domainEntityType,
          );
          if (
            !includedEntity ||
            !includedEntity?.schema ||
            includedEntity.status === 'error'
          ) {
            throw new Error(
              `Unable to find schema for entity type: ${incEntity.attributes.entityMetadata.domainEntityType}`,
            );
          }
          const includedEntitySchema =
            getConfigEntitySchemaWithDomainEntityAttributes(
              entity.schema,
              includedEntity.schema,
            );
          return processIncludedEntity(
            incEntity,
            includedEntitySchema,
          ) as ConfigEntityDetail;
        }) ?? [];

      return deepCloneRequestFetch({
        body: JSON.stringify({
          data: requestDataWithGeneratedIds,
          included:
            finalIncludedEntities.length > 0
              ? finalIncludedEntities
              : undefined,
        }),
        headers: buildConfigEntityAuthHeaders(
          entity.domainEntity.type,
          ConfigEntityAuthAction.Clone,
        ),
      });
    };
  }

  const {
    data: deepCloneGetData,
    isError: isGetError,
    error: fetchErrorMessage,
    isSuccess: isGetSuccess,
    isFetching: isGetFetching,
  } = useQuery({
    queryKey: [deepClonePrefetchOperation?.path, instanceId],
    queryFn: prefetchQueryFn,
    enabled: !!instanceId && !!prefetchQueryFn,
  });

  const {
    data: deepCloneResponse,
    mutate: deepCloneEntity,
    isError: isDeepCloneRequestError,
    isSuccess: isDeepCloneRequestSuccess,
    isPending: isDeepCloneRequestPending,
  } = useMutation({
    mutationFn,
    onSettled: options?.request?.onSettled,
    onMutate: () => {
      options?.request?.onMutate?.('deepClone');
    },
    onSuccess: (responseData) => {
      options?.request?.onSuccess?.(responseData, 'staging');
    },
    onError: options?.request?.onError,
  });

  return {
    deepCloneGetData,
    deepCloneResponse,
    isGetError,
    isGetSuccess,
    isGetFetching,
    fetchErrorMessage,
    deepCloneEntity,
    isDeepCloneRequestError,
    isDeepCloneRequestSuccess,
    isDeepCloneRequestPending,
    enableAction:
      Boolean(instanceId) &&
      Boolean(deepCloneEndpoint) &&
      hasEntityOperation(entity, 'create'),
  };
};
